import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, Check, ChevronDown, ChevronRight, Clock } from 'lucide-react';
import { T, ROLES, springs } from '../../lib/tokens';
import { apiFetch } from '../../lib/api';
import { Glass } from '../glass/Glass';
import { Pill, RoleBadge } from '../primitives/Badge';

const PRIORITY_MAP = {
  1: { label: 'Critical', color: T.red },
  2: { label: 'High', color: T.amber },
  3: { label: 'Medium', color: T.blue },
};

const fmtDur = (m) => m >= 60 ? `${Math.floor(m/60)}h${m%60 ? ` ${m%60}m` : ''}` : `${m}m`;

export const AIProjectGenerator = ({ roles, onClose, onCreated }) => {
  const [step, setStep] = useState('describe'); // 'describe' | 'generating' | 'review' | 'saving'
  const [description, setDescription] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState(null);

  const generate = async () => {
    if (!description.trim()) return;
    setStep('generating');
    setError(null);
    try {
      const res = await apiFetch('/ai/generate-project', {
        method: 'POST',
        body: JSON.stringify({ description: description.trim(), workspace }),
      });
      const data = await res.json();
      if (!res.ok || !data.plan) throw new Error(data.error || 'Generation failed');
      setPlan(data.plan);
      setStep('review');
    } catch (err) {
      setError(err.message);
      setStep('describe');
    }
  };

  const save = async () => {
    setStep('saving');
    try {
      const roleId = workspace ? roles.find(r => r.slug === workspace)?.id : null;
      const res = await apiFetch('/ai/save-project', {
        method: 'POST',
        body: JSON.stringify({ plan, role_id: roleId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      onCreated();
      onClose();
    } catch (err) {
      setError(err.message);
      setStep('review');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }} transition={springs.gentle}
        onClick={e => e.stopPropagation()}
        style={{
          width: 560, maxHeight: '80vh', overflowY: 'auto',
          background: '#fff', borderRadius: 16, padding: 28, boxShadow: T.shadowLg,
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #D4921E, #C86878)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={16} color="#fff" />
          </div>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text }}>AI Project Generator</h2>
            <p style={{ fontSize: 12, color: T.textMuted }}>Describe your project and AI will structure it</p>
          </div>
        </div>

        {/* Step 1: Describe */}
        {step === 'describe' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Describe your project in natural language...&#10;&#10;e.g. &quot;Launch a creator outreach campaign for Liken. Need to research potential creators, build an email list, draft outreach templates, send initial batch, and follow up.&quot;"
              rows={5} autoFocus
              style={{
                padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.10)',
                fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
                lineHeight: 1.5,
              }}
            />

            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 6, display: 'block' }}>Workspace</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {Object.entries(ROLES).map(([slug, r]) => (
                  <Pill key={slug} active={workspace === slug} color={r.color} bg={r.light} border={r.border}
                    onClick={() => setWorkspace(workspace === slug ? '' : slug)}>
                    {r.label}
                  </Pill>
                ))}
              </div>
            </div>

            {error && <div style={{ fontSize: 12, color: T.red, padding: '8px 12px', borderRadius: 8, background: T.redLight }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)', background: 'transparent', color: T.textMid, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={generate} disabled={!description.trim()} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #D4921E, #C86878)', color: '#fff',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                opacity: description.trim() ? 1 : 0.5,
              }}>
                <Sparkles size={14} /> Generate Plan
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Generating */}
        {step === 'generating' && (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
              <Loader2 size={28} color={T.amber} />
            </motion.div>
            <div style={{ fontSize: 14, color: T.textMid, marginTop: 12 }}>Generating project plan...</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>This takes a few seconds</div>
          </div>
        )}

        {/* Step 3: Review */}
        {(step === 'review' || step === 'saving') && plan && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{plan.title}</div>
              {plan.description && <div style={{ fontSize: 13, color: T.textMid, marginTop: 4, lineHeight: 1.5 }}>{plan.description}</div>}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 10, fontWeight: 600, color: PRIORITY_MAP[plan.priority]?.color || T.blue }}>
                  {PRIORITY_MAP[plan.priority]?.label || 'Medium'}
                </span>
                <span style={{ fontSize: 10, color: T.textMuted }}>
                  {plan.stages?.length || 0} stages · {plan.stages?.reduce((a, s) => a + (s.tasks?.length || 0), 0) || 0} tasks
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              {plan.stages?.map((stage, si) => (
                <StagePreview key={si} stage={stage} index={si} />
              ))}
            </div>

            {error && <div style={{ fontSize: 12, color: T.red, padding: '8px 12px', borderRadius: 8, background: T.redLight, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button onClick={() => { setStep('describe'); setPlan(null); }} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)', background: 'transparent', color: T.textMid, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Regenerate</button>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.10)', background: 'transparent', color: T.textMid, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>Cancel</button>
                <button onClick={save} disabled={step === 'saving'} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 8, border: 'none',
                  background: T.green, color: '#fff', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                  opacity: step === 'saving' ? 0.5 : 1,
                }}>
                  {step === 'saving' ? <Loader2 size={14} /> : <Check size={14} />}
                  {step === 'saving' ? 'Saving...' : 'Create Project'}
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

const StagePreview = ({ stage, index }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const totalMin = (stage.tasks || []).reduce((a, t) => a + (t.duration_minutes || 30), 0);

  return (
    <div style={{ borderRadius: 10, border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
      <button onClick={() => setExpanded(!expanded)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '10px 14px', border: 'none', background: 'rgba(0,0,0,0.02)',
        cursor: 'pointer', fontFamily: 'inherit',
      }}>
        {expanded ? <ChevronDown size={12} color={T.textMuted} /> : <ChevronRight size={12} color={T.textMuted} />}
        <div style={{ width: 20, height: 20, borderRadius: 6, background: T.blueLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: T.blue }}>{index + 1}</div>
        <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 600, color: T.text }}>{stage.title}</span>
        <span style={{ fontSize: 10, color: T.textMuted }}>{stage.tasks?.length || 0} tasks · {fmtDur(totalMin)}</span>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <div style={{ padding: '4px 14px 10px' }}>
              {(stage.tasks || []).map((task, ti) => (
                <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: ti < stage.tasks.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}>
                  <div style={{ width: 14, height: 14, borderRadius: 4, border: '1.5px solid rgba(0,0,0,0.12)', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: T.text, flex: 1 }}>{task.title}</span>
                  <span style={{ fontSize: 10, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Clock size={9} /> {fmtDur(task.duration_minutes || 30)}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
